import {
  Avatar,
  Box,
  Checkbox,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import moment from 'moment';
import * as React from 'react';
import { ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useLoggedInEmployeeId, useLoggedInUserRoles } from '~/components/Auth';
import { People } from '~/services/Http';
import { isRoleHRAdminFull } from '~/services/Permissions';
import { capitalizeFirstLetter } from '~/utils/helper';

import { WorklistRowMode } from '../../types';

import { useSxStyles } from './useStyles';

interface Props {
  tasks: People.WorklistTaskDto[];
  selectedTasks: Record<number, boolean>;
  setSelectedTasks: React.Dispatch<
    React.SetStateAction<Record<number, boolean>>
  >;
  rowMode: WorklistRowMode;
  myWorklists: boolean;
}

export const WorklistPanelTasks: React.FC<Props> = ({
  tasks,
  selectedTasks,
  setSelectedTasks,
  rowMode,
  myWorklists,
}) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();

  const handleCheckboxChange = (id: number) => {
    setSelectedTasks((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const loggedInEmployeeId = useLoggedInEmployeeId();

  const myRoles = useLoggedInUserRoles();
  const isHRAdmin = isRoleHRAdminFull(myRoles);

  const taskLabel = {
    subject: t('laSubject'),
    status: t('status'),
    responsible: t('responsible'),
    dueDate: capitalizeFirstLetter(t('dueOn')),
  };

  const isTableMode = rowMode === WorklistRowMode.Table;

  const ItemValue = useCallback(
    (itemProps: { label: ReactNode; children: ReactNode }) => {
      const { label, children } = itemProps;

      if (!children) {
        return <></>;
      }

      return (
        <Box
          sx={
            rowMode === WorklistRowMode.SemiItem ? sxStyles.semiItem : undefined
          }
        >
          <Box sx={sxStyles.itemLabel}>{label}: </Box>
          <Box>{children}</Box>
        </Box>
      );
    },
    [rowMode, sxStyles],
  );

  return (
    <Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={sxStyles.headerRow}>
              <TableCell sx={sxStyles.tableCellHeader}></TableCell>
              {isTableMode ? (
                <>
                  <TableCell sx={sxStyles.tableCellHeader}>
                    <strong>{taskLabel.subject}</strong>
                  </TableCell>
                  <TableCell sx={sxStyles.tableCellHeader}>
                    <strong>{taskLabel.status}</strong>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...sxStyles.tableCellHeader,
                      ...sxStyles.responsibleHeader,
                    }}
                  >
                    <strong>{taskLabel.responsible}</strong>
                  </TableCell>
                  <TableCell
                    sx={{ ...sxStyles.tableCellHeader, ...sxStyles.dueHeader }}
                  >
                    <strong>{taskLabel.dueDate}</strong>
                  </TableCell>
                </>
              ) : (
                <TableCell sx={sxStyles.tableCellHeader}>
                  {t('tasks')}
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tasks.map((task) => {
              const renderTask = {
                status: task.status?.code ? `${task.status.value}` : '',
                assignee: task.assignee ? (
                  <Box sx={sxStyles.flex}>
                    <Avatar
                      alt={task.assignee.firstName}
                      src={task.assignee.profilePic}
                      className={'avatar'}
                      data-cy="profile-pic"
                      sx={sxStyles.avatar}
                    />
                    {task.assignee.firstName} {task.assignee.lastName}
                  </Box>
                ) : (
                  ''
                ),
                dueDate: task.dueDate
                  ? moment(task.dueDate).format('DD.MM.YYYY')
                  : '',
              };

              return (
                <TableRow
                  key={task.id}
                  sx={{
                    ...sxStyles.tableRow,
                    ...(selectedTasks[task.id]
                      ? sxStyles.selectedTableRow
                      : {}),
                  }}
                >
                  <TableCell
                    sx={{ ...sxStyles.tableCellRoot, ...sxStyles.smallCell }}
                  >
                    <Checkbox
                      checked={!!selectedTasks[task.id]}
                      onChange={() => handleCheckboxChange(task.id)}
                      disabled={
                        !isHRAdmin &&
                        myWorklists &&
                        Number(task.assignee?.employeeId) !== loggedInEmployeeId
                      }
                    />
                  </TableCell>
                  {isTableMode ? (
                    <>
                      <TableCell
                        sx={{ ...sxStyles.tableCellRoot, ...sxStyles.textCell }}
                      >
                        <Box sx={sxStyles.textClamped}>{task.title}</Box>
                      </TableCell>
                      <TableCell
                        sx={{
                          ...sxStyles.tableCellRoot,
                          ...sxStyles.statusCell,
                        }}
                      >
                        {renderTask.status}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...sxStyles.tableCellRoot,
                          ...sxStyles.responsibleCell,
                        }}
                      >
                        {renderTask.assignee}
                      </TableCell>
                      <TableCell
                        sx={{ ...sxStyles.tableCellRoot, ...sxStyles.dueCell }}
                      >
                        {renderTask.dueDate}
                      </TableCell>
                    </>
                  ) : (
                    <TableCell
                      sx={{ ...sxStyles.tableCellRoot, ...sxStyles.itemCell }}
                    >
                      <Box sx={sxStyles.itemValues}>
                        <ItemValue label={taskLabel.subject}>
                          {task.title}
                        </ItemValue>
                        <ItemValue label={taskLabel.status}>
                          {renderTask.status}
                        </ItemValue>
                        <ItemValue label={taskLabel.responsible}>
                          {renderTask.assignee}
                        </ItemValue>
                        <ItemValue label={taskLabel.dueDate}>
                          {renderTask.dueDate}
                        </ItemValue>
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
